import React,{useState,useEffect} from 'react';
import {useParams,useNavigate} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import api from '../services/api';
import {fmtDateTime} from '../utils/helpers';
import {useTranslation} from 'react-i18next';
import FileShaClaimButton from '../components/common/FileShaClaimButton';

const safeArray=(v:any)=>Array.isArray(v)?v:(v?[v]:[]);

export default function LabResultEntryPage(){
  const {t}=useTranslation();
  const {id}=useParams<{id:string}>();
  const nav=useNavigate();
  const [resultOrder,setResultOrder]=useState<any>(null);
  const [resultForm,setResultForm]=useState<any>({
    resultData:'', verifiedBy:'', verifiedAt:'', enteredBy:'',
    isCritical:false, isAbnormal:false, requestId:'', testId:'',
    normalRange:'', unit:'', notes:''
  });
  const [resultParams,setResultParams]=useState<any[]>([]);
  const [formulas,setFormulas]=useState<any[]>([]);
  const [autoInterpretations,setAutoInterpretations]=useState<any[]>([]);

  // Load formula master on mount
  useEffect(()=>{
    api.getLabFormulas?.().then((r:any)=>setFormulas(Array.isArray(r?.data)?r.data:[])).catch(()=>{});
  },[]);

  // Formula auto-calculator — runs when any result value changes
  const runFormulas=(params:any[])=>{
    if(!formulas.length) return params;
    const valueMap:Record<string,number>={};
    params.forEach(p=>{
      if(p.parameterCode||p.parameterName){
        const key=(p.parameterCode||p.parameterName||'').toUpperCase();
        const val=parseFloat(p.resultValue);
        if(!isNaN(val)) valueMap[key]=val;
      }
    });
    // Simple formula evaluator using Function constructor (safe - only math)
    return params.map(p=>{
      const formula=formulas.find((f:any)=>
        (f.output_parameter_code||'').toUpperCase()===(p.parameterCode||'').toUpperCase()
      );
      if(formula&&p.resultValue===''){
        try{
          const expr=formula.formula_expression
            .replace(/([A-Z_]+)/g,(_:string,k:string)=>valueMap[k]!==undefined?String(valueMap[k]):k);
          // Only evaluate if all variables are resolved (no alpha chars remain)
          if(!/[a-zA-Z]/.test(expr)){
            // eslint-disable-next-line no-new-func
            const result=Function('"use strict";return ('+expr+')')();
            if(!isNaN(result)&&isFinite(result)){
              return {...p,resultValue:result.toFixed(2),isCalculated:true};
            }
          }
        }catch{}
      }
      return p;
    });
  };

  // Auto-interpretation: call backend after value changes settle
  const [interpTimer,setInterpTimer]=useState<any>(null);
  const triggerAutoInterpret=(params:any[])=>{
    if(interpTimer) clearTimeout(interpTimer);
    const timer=setTimeout(async()=>{
      const toInterpret=params.filter(p=>p.resultValue&&!isNaN(parseFloat(p.resultValue))).map(p=>({
        parameterCode:p.parameterCode||p.parameterName||'',
        resultValue:parseFloat(p.resultValue),
        unit:p.unit,
      }));
      if(!toInterpret.length) return;
      try{
        const r=await api.interpretLabResults?.(toInterpret);
        if(r?.data?.length){
          setAutoInterpretations(r.data);
          // Auto-fill interpretation textarea with generated text
          const text=r.data.map((i:any)=>`${i.parameterCode}: ${i.interpretation}`).join('\n');
          setInterpretation(prev=>prev||text);
        }
      }catch{}
    },1500);
    setInterpTimer(timer);
  };
  const [interpretation,setInterpretation]=useState('');
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [masterLoaded,setMasterLoaded]=useState(false);
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const loadOrder=async()=>{
    if(!id)return;
    setLoading(true);
    try{
      const r=await api.getLabOrder(id);
      const order=r.data;
      setResultOrder(order);

      const tests=safeArray(order.tests);
      let rows:any[]=[];
      let foundMasterParams=false;

      // For old orders where testId was not stored, try to resolve it by name from Lab Tests master
      const resolvedTests=await Promise.all(tests.map(async(t:any)=>{
        if(t.testId) return t;
        const searchKey=t.testCode||t.testName||t.name||'';
        if(!searchKey) return t;
        try{
          const lr=await api.getLabTests({search:searchKey,take:3});
          const list=Array.isArray(lr.data)?lr.data:(lr.data?.data||lr.data?.tests||[]);
          const exact=list.find((x:any)=>
            (x.testName||x.test_name||'').toLowerCase()===searchKey.toLowerCase()||
            (x.testCode||x.test_code||'').toLowerCase()===searchKey.toLowerCase()
          );
          if(exact) return {...t,testId:exact.id};
          if(list.length>0) return {...t,testId:list[0].id};
        }catch{}
        return t;
      }));

      // Try to auto-load full parameter panels from Lab Master for each ordered test
      for(const t of resolvedTests){
        if(t.testId){
          try{
            const pr=await api.getLabParameters({testId:t.testId});
            const params=pr?.data||[];
            if(params.length>0){
              foundMasterParams=true;
              params.forEach((p:any)=>{
                const ranges=p.reference_ranges||p.referenceRanges||[];
                const defaultRange=ranges.find((rg:any)=>rg.gender==='ALL'||!rg.gender)||ranges[0];
                rows.push({
                  testId:t.testId,testName:t.testName||t.name,
                  parameterId:p.id,
                  parameterName:p.display_name||p.displayName||p.parameter_name||p.parameterName,
                  resultValue:'',
                  unit:p.unit||'',
                  normalRangeLow:defaultRange?.normal_min||defaultRange?.normalMin||'',
                  normalRangeHigh:defaultRange?.normal_max||defaultRange?.normalMax||'',
                  normalRangeText:p.normal_value||p.normalValue||'',
                  dataType:p.data_type||p.dataType||'NUMERIC',
                  options:p.options||[],
                  isCritical:false,isAbnormal:false,
                });
              });
            }
          }catch{}
        }
      }

      // Fallback: if no master parameters found for any test, use one row per ordered test
      if(!foundMasterParams){
        resolvedTests.forEach((t:any)=>{
          rows.push({testId:t.testId,testName:t.testName||t.name,parameterName:t.testName||t.name,resultValue:'',unit:'',normalRangeLow:'',normalRangeHigh:'',normalRangeText:t.normalRange||'',isAbnormal:false,isCritical:false});
        });
      }
      if(!rows.length){
        rows.push({testId:'',testName:'',parameterName:'Result',resultValue:'',unit:'',normalRangeLow:'',normalRangeHigh:'',normalRangeText:'',isAbnormal:false,isCritical:false});
      }

      setMasterLoaded(foundMasterParams);
      setResultParams(rows);
      setInterpretation('');
    }catch(e:any){
      notify('❌ Failed to load order');
    }
    setLoading(false);
  };

  useEffect(()=>{loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[id]);

  const submitResults=async(verifyNow=false)=>{
    if(!resultOrder)return;
    setSaving(true);
    try{
      const tests=safeArray(resultOrder.tests);
      const testId=tests[0]?.testId||'';
      const saved=await api.saveLabResult({
        requestId:resultOrder.id,testId,
        patientId:resultOrder.patient_id||resultOrder.patientId,
        interpretation,parameters:resultParams,
        isCritical:resultParams.some(p=>p.isCritical),
        isAbnormal:resultParams.some(p=>p.isAbnormal),
      });
      if(verifyNow){
        const resultId=saved?.data?.id||saved?.data?.resultId||saved?.data?.result?.id;
        try{ await api.verifyResult(resultId||resultOrder.id,{level:'PATHOLOGIST',remarks:interpretation}); }
        catch{ await api.verifyResult(resultOrder.id,{level:'PATHOLOGIST',remarks:interpretation}); }
        notify('✅ Results verified & approved by Pathologist!');
      }else{
        notify('✅ Results saved — pending pathologist approval.');
      }
      setTimeout(()=>nav('/laboratory'),900);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  if(loading){
    return <Layout title="Enter Lab Results"><div className="p-12 text-center text-slate-400">Loading order...</div></Layout>;
  }
  if(!resultOrder){
    return <Layout title="Enter Lab Results"><div className="p-12 text-center text-slate-400">Order not found.<br/><button onClick={()=>nav('/laboratory')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl text-sm border border-slate-200 mt-4">← Back to Laboratory</button></div></Layout>;
  }

  const patientName=resultOrder.patient_first_name?`${resultOrder.patient_first_name} ${resultOrder.patient_last_name||''}`:'';

  return(
    <Layout title={`Enter Results — ${resultOrder.request_number||resultOrder.requestNumber||''}`} actions={
      <div className="flex gap-2 items-center">
        <FileShaClaimButton
          patientId={resultOrder.patient_id||resultOrder.patientId}
          patientName={patientName}
          encounterId={resultOrder.id}
          serviceName={safeArray(resultOrder.tests).map((t:any)=>t.testName).filter(Boolean).join(', ')||'Laboratory'}
          from="laboratory"
        />
        <button onClick={()=>nav('/laboratory')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl text-sm border border-slate-200">← Back to Laboratory</button>
      </div>
    }>
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      <div className="max-w-5xl mx-auto space-y-4">
        {/* Order info strip */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex justify-between items-center">
          <div>
            <p className="font-semibold">{patientName||'—'}</p>
            <p className="text-xs font-mono text-[#02357A]">{resultOrder.uhid||resultOrder.patient_id||resultOrder.patientId}</p>
          </div>
          <div className="text-right text-sm">
            <p className="text-slate-500">Ordered: {fmtDateTime(resultOrder.created_at||resultOrder.createdAt)}</p>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-[#02357A] mt-1">{resultOrder.status}</span>
          </div>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
          Tests: {safeArray(resultOrder.tests).map((t:any)=>t.testName).join(', ')}
          {masterLoaded&&<span className="ml-2 text-green-600 font-medium">✓ Parameters auto-loaded from Lab Master</span>}
          {!masterLoaded&&(
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-amber-600 font-medium">⚠️ No Lab Master parameters found — using test name as single result.</span>
              {safeArray(resultOrder.tests).filter((t:any)=>t.testId).map((t:any)=>(
                <button key={t.testId}
                  onClick={()=>nav('/lab/parameter-master',{state:{testId:t.testId,testName:t.testName}})}
                  className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 px-2.5 py-1 rounded-full font-semibold">
                  🔗 Set up parameters for {t.testName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Result entry table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 mb-2 px-1">
            <span className="col-span-3">Parameter</span>
            <span className="col-span-2">Result Value</span>
            <span className="col-span-2">Unit</span>
            <span className="col-span-3">Normal Range</span>
            <span className="col-span-2">Flag</span>
          </div>
          {resultParams.map((p:any,i:number)=>{
            const low=parseFloat(p.normalRangeLow);const high=parseFloat(p.normalRangeHigh);const val=parseFloat(p.resultValue);
            const isAbnormal=p.resultValue&&!isNaN(val)&&!isNaN(low)&&!isNaN(high)?(val<low||val>high):false;
            const isCritical=isAbnormal&&(val<low*0.7||val>high*1.3);
            return(
              <div key={i} className={`grid grid-cols-12 gap-2 items-center p-2 rounded mb-1 ${isCritical?'bg-red-50 border border-red-200':isAbnormal?'bg-amber-50':'bg-slate-50'}`}>
                <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#039089] transition-all col-span-3" value={p.parameterName} onChange={e=>setResultParams(resultParams.map((x,xi)=>xi===i?{...x,parameterName:e.target.value}:x))}/>
                {p.dataType==='OPTIONS'&&p.options?.length>0?(
                  <select className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#039089] transition-all col-span-2" value={p.resultValue} onChange={e=>setResultParams(resultParams.map((x,xi)=>xi===i?{...x,resultValue:e.target.value}:x))}>
                    <option value="">Select...</option>
                    {p.options.map((opt:string)=><option key={opt}>{opt}</option>)}
                  </select>
                ):(
                  <div className="col-span-2 relative">
                    <input className={`input w-full text-sm font-semibold ${isCritical?'text-red-700 border-red-400':isAbnormal?'text-amber-700 border-amber-400':''} ${p.isCalculated?'bg-blue-50 border-blue-300':''}`}
                      value={p.resultValue}
                      onChange={e=>{const updated=resultParams.map((x,xi)=>xi===i?{...x,resultValue:e.target.value,isAbnormal,isCritical,isCalculated:false}:x);const withFormulas=runFormulas(updated);setResultParams(withFormulas);triggerAutoInterpret(withFormulas);}}
                      placeholder="Value"/>
                    {p.isCalculated&&<span className="absolute right-1 top-1 text-xs text-blue-500 font-bold" title="Auto-calculated by formula">ƒ</span>}
                  </div>
                )}
                <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#039089] transition-all col-span-2" value={p.unit} onChange={e=>setResultParams(resultParams.map((x,xi)=>xi===i?{...x,unit:e.target.value}:x))} placeholder="Unit"/>
                <div className="col-span-3 flex gap-1 items-center">
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#039089] transition-all flex-1" value={p.normalRangeLow} onChange={e=>setResultParams(resultParams.map((x,xi)=>xi===i?{...x,normalRangeLow:e.target.value}:x))} placeholder="Low"/>
                  <span className="text-slate-400 text-xs">–</span>
                  <input className="w-full bg-white border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#039089] transition-all flex-1" value={p.normalRangeHigh} onChange={e=>setResultParams(resultParams.map((x,xi)=>xi===i?{...x,normalRangeHigh:e.target.value}:x))} placeholder="High"/>
                </div>
                <span className={`col-span-2 text-xs font-bold ${isCritical?'text-red-700':isAbnormal?'text-amber-700':'text-green-600'}`}>
                  {isCritical?'⚠️ CRIT':isAbnormal?(val<low?'↓ LOW':'↑ HIGH'):'Normal'}
                </span>
              </div>
            );
          })}
          <button type="button" onClick={()=>setResultParams([...resultParams,{parameterName:'',resultValue:'',unit:'',normalRangeLow:'',normalRangeHigh:'',normalRangeText:'',isAbnormal:false,isCritical:false}])} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 rounded-xl text-sm border border-slate-200 mt-2">+ Add Parameter</button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          {autoInterpretations.length>0&&(
            <div className="mb-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-[#02357A] mb-2">🤖 Auto-Interpretation (based on rules)</p>
              <div className="space-y-1">
                {autoInterpretations.map((item:any,i:number)=>(
                  <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded-lg ${item.severity==='CRITICAL'?'bg-red-100 text-red-800':item.severity==='SEVERE'?'bg-red-50 text-red-700':item.severity==='MODERATE'?'bg-amber-50 text-amber-800':item.severity==='MILD'?'bg-yellow-50 text-yellow-800':'bg-green-50 text-green-700'}`}>
                    <span className="font-bold flex-shrink-0">{item.parameterCode}:</span>
                    <span>{item.interpretation}</span>
                  </div>
                ))}
              </div>
              <button onClick={()=>setInterpretation(autoInterpretations.map((i:any)=>i.parameterCode+': '+i.interpretation).join('\n'))} className="mt-2 text-xs text-[#02357A] hover:underline">📋 Copy to remarks</button>
            </div>
          )}
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Interpretation / Remarks</label>
          <textarea className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#039089] transition-all" rows={3} value={interpretation} onChange={e=>setInterpretation(e.target.value)} placeholder="Clinical interpretation, pathologist remarks... (auto-fills from rules above)"/>
        </div>

        <div className="flex gap-3 pb-8">
          <button onClick={()=>submitResults(false)} disabled={saving} className="bg-[#039089] hover:bg-teal-700 text-white font-bold py-3 px-4 rounded-xl text-base transition-all flex-1 disabled:opacity-50">{saving?'Saving...':'Save Results'}</button>
          <button onClick={()=>submitResults(true)} disabled={saving} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-6 rounded-xl text-sm border border-slate-200">Save & Verify (Pathologist)</button>
          <button onClick={()=>nav('/laboratory')} className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-6 rounded-xl text-sm border border-slate-200">{t('common.cancel','Cancel')}</button>
        </div>
      </div>
    </Layout>
  );
}
